import { z } from "zod";

export const OfferForm = z.object({
  customer: z.object({
    email: z.string().email(),
    mobile: z.string().min(1, { message: "Required field" }),
    firstName: z.string().min(1, { message: "Required field" }),
    lastName: z.string(),
    createDeal: z.boolean(),
  }),
  description: z.object({
    title: z.string().min(1, { message: "Required field" }),
    intro: z.string().optional(),
    currency: z.string().min(3),
    charterType: z.enum(["bareboat", "skippered", "crewed"]).or(z.string().min(1)),
    enquiryDate: z.string().nullable(),
    broker: z.object({
      id: z.number().min(1),
      fullName: z.string().min(1),
      email: z.string().min(1),
    }),
    guests: z.number().min(1),
    brand: z.enum(["helm", "anchor"]),
  }),
  yachts: z
    .array(
      z.object({
        paymentSteps: z.array(
          z.object({
            amount: z.number(),
            operatorAmount: z.number(),
            originalPercentage: z.number().optional().nullable(),
            isNewStep: z.boolean().optional(),
            days: z.number().int().nonnegative(),
            type: z.enum(["on confirmation", "after confirmation", "before booking date"]),
            scheduleStep: z.number(),
            isNewOffer: z.boolean().optional(),
          }),
        ),
        cardPayments: z.boolean(),
        startDate: z.string(),
        endDate: z.string(),
        startTime: z.string(),
        endTime: z.string(),
        discountCharterPrice: z.number(),
        yachtOnHold: z.boolean(),
        ignoreAvailability: z.boolean(),
        brokerNote: z.string().nullable().optional(),
        holdExpiresAt: z.string().nullable().optional(),
        order: z.number().optional().nullable(),
        startLocation: z.object({
          id: z.string().min(1),
          names: z.string().min(1),
        }),
        endLocation: z.object({
          id: z.string().min(1),
          names: z.string().min(1),
        }),
        amenitiesDetails: z
          .array(
            z.object({
              id: z.string().optional(),
              paidLocally: z.boolean(),
              price: z.number(),
              obligatory: z.boolean(),
              text: z.string().optional(),
              description: z.string().optional(),
              number: z.number().optional().nullable(),
              provider: z.enum(["NS", "CYA", "BM", "Helm"]).optional(),
              checked: z.boolean(),
              currency: z.string().optional(),
              currencyId: z.string().optional(),
              hidden: z.boolean().optional(),
              freeOfCharge: z.boolean().optional(),
              included: z.boolean().optional(),
              isCustomAmenity: z.boolean().optional(),
              currencyCode: z.string().optional(),
              finalBookingPrice: z.number().optional(),
              formula: z.string().optional(),
            }),
          )
          .or(z.tuple([]))
          .optional(),
        customAmenities: z
          .array(
            z.object({
              id: z.string(),
              dedupeKey: z.string().optional(),
              text: z.string(),
              comment: z.string().optional(),
              included: z.boolean().optional(),
              obligatory: z.boolean(),
              paid: z.boolean().optional(),
              paidLocally: z.boolean(),
              price: z.number(),
              finalBookingPrice: z.number(),
              number: z.number(),
              isPercentage: z.boolean().optional(),
              manuallyAssigned: z.boolean().optional(),
              per: z.string().optional(),
              percentageType: z.string().optional(),
              currencyId: z.string(),
              currencyCode: z.string(),
              yachtId: z.string().optional(),
              isCustomAmenity: z.boolean(),
              checked: z.boolean(),
              hidden: z.boolean().optional(),
              freeOfCharge: z.boolean().optional(),
              yachtIndex: z.number().optional(),
            }),
          )
          .or(z.tuple([]))
          .optional(),
        yacht: z.any(),
        pricing: z.object({
          discountPrice: z.number(),
          discountPercentage: z.number(),
          depositPrice: z.number(),
          totalPrice: z.number(),
          originalPrice: z.number(),
          clientPrice: z.number(),
          clientPriceApi: z.number(),
          helmCustomDiscountPrice: z.number(),
          operatorPayablePrice: z.number(),
          operatorTotalPrice: z.number().optional(),
          extrasPriceInOperatorCurrency: z.number().optional(),
          commissionPostHelmDiscountPrice: z.number(),
          includedFree: z.array(z.object({ text: z.string() })).or(z.tuple([])),
          pickupObligatory: z.array(z.object({ text: z.string(), price: z.number() })).or(z.tuple([])),
          advanceObligatory: z.array(z.object({ text: z.string(), price: z.number() })).or(z.tuple([])),
          advanceExtras: z.array(z.object({ text: z.string(), price: z.number() })).or(z.tuple([])),
          pickupExtras: z.array(z.object({ text: z.string(), price: z.number() })).or(z.tuple([])),
          yachtCurrencyCode: z.string().optional().nullable(),
          vatPrice: z.number().optional(),
          vatPercentage: z.string().optional(),
          apaPrice: z.number().optional(),
          apaPercentage: z.string().optional(),
          deliveryFeesPrice: z.number().optional(),
          charterTerms: z.enum(["inclusive", "exclusive"]).optional(),
        }),
        id: z.number().optional(),
      }),
    )
    .min(1, { message: "At least one yacht is required" }),
  hubspotId: z.string().optional(),
  mailing: z.object({
    bookingConfirmation: z.boolean(),
    bookingUpdates: z.boolean(),
    paymentConfirmations: z.boolean(),
    paymentReminders: z.boolean(),
  }),
});

export type OfferForm = z.infer<typeof OfferForm>;

export const emptyOfferForm: OfferForm = {
  customer: {
    email: "",
    mobile: "",
    firstName: "",
    lastName: "",
    createDeal: false,
  },
  description: {
    title: "",
    intro: "",
    currency: "EUR",
    charterType: "",
    enquiryDate: null,
    guests: 1,
    broker: {
      id: 0,
      fullName: "",
      email: "",
    },
    brand: "helm",
  },
  yachts: [],
  mailing: {
    bookingConfirmation: true,
    bookingUpdates: true,
    paymentConfirmations: true,
    paymentReminders: true,
  },
};
